import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import useAuth from "../../../../auth/useAuth";
import { useFetchGET } from "../../../../state/FetchHelpers";
import { automaticRoutesListRoute, createEmptyRoute } from "../../../../config";
import { Alerts } from "../../../../components/alerts/Alerts.tsx";

const BASE_ORIGIN_OPTIONS = ["Winchester", "Sterling"];

const MoveOrderDialog = ({ open, onClose, currentRouteId, currentRouteCreationDate, onConfirmMove }) => {
  const auth = useAuth();
  const token = auth.globals.token;
  const { showErrorRequest } = Alerts();

  const [mode, setMode] = useState("existing");
  const [targetRouteId, setTargetRouteId] = useState("");
  const [newRouteBaseOrigin, setNewRouteBaseOrigin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { data: routesResponse, loading: routesLoading } = useFetchGET(
    open ? automaticRoutesListRoute("pendiente_aprobacion", currentRouteCreationDate) : undefined,
    token,
    []
  );

  const otherRoutes = (Array.isArray(routesResponse?.routes) ? routesResponse.routes : []).filter(
    (r) => r.id !== currentRouteId && r.creation_date === currentRouteCreationDate
  );

  useEffect(() => {
    if (open) {
      setMode("existing");
      setTargetRouteId("");
      setNewRouteBaseOrigin("");
    }
  }, [open]);

  const handleConfirmExisting = async () => {
    if (!targetRouteId) return;
    setSubmitting(true);
    await onConfirmMove(targetRouteId);
    setSubmitting(false);
  };

  const handleCreateAndMove = async () => {
    if (!newRouteBaseOrigin) return;
    try {
      setSubmitting(true);
      const resp = await createEmptyRoute(token, newRouteBaseOrigin, currentRouteCreationDate);
      if (resp.ok) {
        const data = await resp.json();
        await onConfirmMove(data.route_id);
      } else if (resp.status === 401 || resp.status === 403) {
        showErrorRequest("No tiene autorización o su sesión ha expirado", auth);
      } else {
        const errorData = await resp.json().catch(() => null);
        showErrorRequest(errorData?.detail || errorData?.message || "No fue posible crear la ruta nueva", auth);
      }
    } catch (e) {
      showErrorRequest(e.message || "Error de conexión", auth);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = () => (mode === "existing" ? handleConfirmExisting() : handleCreateAndMove());
  const confirmDisabled =
    submitting || (mode === "existing" ? !targetRouteId : !newRouteBaseOrigin);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mover orden a otra ruta</DialogTitle>
      <DialogContent>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="existing" control={<Radio />} label="Mover a una ruta existente" />
          <FormControlLabel value="new" control={<Radio />} label="Crear una nueva ruta" />
        </RadioGroup>

        {mode === "existing" &&
          (routesLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={28} />
            </Box>
          ) : otherRoutes.length === 0 ? (
            <Typography color="text.secondary" mt={1}>
              No hay otras rutas pendientes de aprobación disponibles.
            </Typography>
          ) : (
            <FormControl fullWidth variant="filled" sx={{ mt: 1 }}>
              <InputLabel id="select-target-route-label">Ruta destino</InputLabel>
              <Select
                labelId="select-target-route-label"
                value={targetRouteId}
                onChange={(e) => setTargetRouteId(e.target.value)}
                size="small"
              >
                {otherRoutes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    Ruta #{r.id} — {r.ring_name || r.base_origin} ({r.total_orders} órdenes)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}

        {mode === "new" && (
          <FormControl fullWidth variant="filled" sx={{ mt: 1 }}>
            <InputLabel id="select-new-route-base-label">Base de origen</InputLabel>
            <Select
              labelId="select-new-route-base-label"
              value={newRouteBaseOrigin}
              onChange={(e) => setNewRouteBaseOrigin(e.target.value)}
              size="small"
            >
              {BASE_ORIGIN_OPTIONS.map((base) => (
                <MenuItem key={base} value={base}>
                  {base}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="secondary" disabled={confirmDisabled}>
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : mode === "existing" ? (
            "Mover orden"
          ) : (
            "Crear ruta y mover"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveOrderDialog;
